import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TYPES_COMMUNICATION } from '@eec/contrats';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';
interface Message {
  id: string;
  titre: string;
  contenu: string;
  statut: 'PENDING_VALIDATION' | 'PUBLISHED' | 'REJECTED' | 'ARCHIVED';
  type: keyof typeof TYPES_COMMUNICATION;
  version: number;
  cree_par: string;
  motif: string | null;
  fin: string | null;
}
const etats = {
  PENDING_VALIDATION: 'À valider',
  PUBLISHED: 'Publié',
  REJECTED: 'Rejeté',
  ARCHIVED: 'Archivé',
};
export function PageCommunication() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [creation, setCreation] = useState(false);
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const q = useQuery({
    queryKey: ['communication-reseau'],
    queryFn: () => requete<{ elements: Message[] }>('/communication-reseau'),
    enabled: peut(contexte, 'communication', 'voir'),
  });
  const provinces = useQuery({
    queryKey: ['provinces'],
    queryFn: () => requete<{ elements: { id: string; nom: string }[] }>('/referentiel/provinces'),
    enabled: creation,
  });
  const incidents = useQuery({
    queryKey: ['incidents-reseau', 'communication'],
    queryFn: () => requete<{ elements: { id: string; titre: string }[] }>('/incidents'),
    enabled: creation && peut(contexte, 'incident', 'voir'),
  });
  async function envoyer(chemin: string, corps: unknown) {
    setErreur('');
    setOccupe(true);
    try {
      await requete(chemin, { methode: 'POST', corps });
      await client.invalidateQueries({ queryKey: ['communication-reseau'] });
      await client.invalidateQueries({ queryKey: ['messages-publics'] });
      setCreation(false);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setOccupe(false);
    }
  }
  if (!peut(contexte, 'communication', 'voir'))
    return <p role="alert">Accès à la communication non autorisé.</p>;
  return (
    <section>
      <EnTetePage titre="Communication publique" />
      <p>
        Un message devient visible sur le portail après validation par une autre personne.
        N’inscrivez ni données personnelles ni localisation d’infrastructures sensibles. Un message
        rejeté peut être remplacé par une nouvelle proposition.
      </p>
      {peut(contexte, 'communication', 'creer') && (
        <button onClick={() => setCreation(!creation)}>Proposer un message</button>
      )}
      {(erreur || q.error) && <p role="alert">{erreur || messageErreur(q.error)}</p>}
      {creation && (
        <form
          style={{ display: 'grid', gap: 12, maxWidth: 750 }}
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const date = (k: string) =>
              f.get(k) ? new Date(String(f.get(k))).toISOString() : null;
            void envoyer('/communication-reseau', {
              type: f.get('type'),
              domaine: f.get('domaine'),
              province_id: f.get('province_id'),
              titre: f.get('titre'),
              contenu: f.get('contenu'),
              incident_id: f.get('incident_id') || null,
              debut: date('debut'),
              fin: date('fin'),
            });
          }}
        >
          <label>
            Type de message{' '}
            <select name="type">
              {Object.entries(TYPES_COMMUNICATION).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label>
            Service concerné{' '}
            <select name="domaine">
              <option value="WATER">Eau</option>
              <option value="ELECTRICITY">Électricité</option>
            </select>
          </label>
          <label>
            Province concernée{' '}
            <select name="province_id" required>
              <option value="">Choisir</option>
              {provinces.data?.elements.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nom}
                </option>
              ))}
            </select>
          </label>
          <label>
            Incident associé{' '}
            <select name="incident_id">
              <option value="">Aucun (consignes ou coupure programmée)</option>
              {incidents.data?.elements.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.titre}
                </option>
              ))}
            </select>
          </label>
          <label>
            Titre public <input name="titre" required minLength={5} maxLength={150} />
          </label>
          <label>
            Texte public{' '}
            <textarea name="contenu" required minLength={10} maxLength={3000} rows={6} />
          </label>
          <label>
            Début de l’événement <input name="debut" type="datetime-local" />
          </label>
          <label>
            Fin de l’événement et de l’affichage <input name="fin" type="datetime-local" />
          </label>
          <p>
            Dates obligatoires pour une coupure programmée. Les heures saisies correspondent au
            fuseau horaire de votre appareil. Le message est affiché dès validation, y compris pour
            annoncer un événement futur.
          </p>
          {(provinces.error || incidents.error) && (
            <p role="alert">{messageErreur(provinces.error || incidents.error)}</p>
          )}
          <button disabled={occupe}>Soumettre à validation</button>
        </form>
      )}
      {q.isPending && <p>Chargement des messages…</p>}
      <p>Les 200 messages les plus récents sont affichés.</p>
      {q.data?.elements.length === 0 && <p>Aucun message enregistré.</p>}
      {q.data?.elements.map((m) => (
        <article key={m.id} style={{ paddingBlock: 20, borderBottom: '1px solid currentColor' }}>
          <h2>{m.titre}</h2>
          <p>
            {etats[m.statut]} · {TYPES_COMMUNICATION[m.type]}
            {m.fin && new Date(m.fin).getTime() <= Date.now()
              ? ' · Période d’affichage terminée'
              : ''}
          </p>
          <p style={{ whiteSpace: 'pre-wrap' }}>{m.contenu}</p>
          {m.motif && <p>Décision : {m.motif}</p>}
          {peut(contexte, 'communication', 'valider') &&
            ['PENDING_VALIDATION', 'PUBLISHED'].includes(m.statut) && (
              <form
                style={{ display: 'grid', gap: 12, maxWidth: 750 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  void envoyer(`/communication-reseau/${m.id}/decision`, {
                    action: f.get('action'),
                    version: m.version,
                    motif: f.get('motif'),
                  });
                }}
              >
                <label>
                  Décision de communication{' '}
                  <select name="action">
                    {m.statut === 'PUBLISHED' ? (
                      <option value="archiver">Retirer du portail</option>
                    ) : (
                      <>
                        {m.cree_par !== contexte?.compte.id && (
                          <option value="publier">Publier sur le portail</option>
                        )}
                        <option value="rejeter">Rejeter la proposition</option>
                      </>
                    )}
                  </select>
                </label>
                <label>
                  Motif de décision <input name="motif" minLength={5} maxLength={1000} required />
                </label>
                <button disabled={occupe}>Enregistrer la décision</button>
              </form>
            )}
        </article>
      ))}
    </section>
  );
}
